'use client';

import {
    motion,
    useMotionValueEvent,
    useSpring,
    useTransform,
    type MotionValue,
} from 'motion/react';
import Image from 'next/image';
import { ReactNode, useRef, useState } from 'react';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import {
    imageLoaderForId,
    pyriteImageStaticPath,
} from '../../../../../domains/Layout/Image/URL/ImageURL';

import style from './Slide.module.css';

type SlideProps = {
    title: string;
    number: string;
    description: string;
    imageSrc?: string;
    imageAlt?: string;
    overlayImageUrl?: string;
    overlayImageAlt?: string;
    index: number;
    totalSlides: number;
    scrollYProgress: MotionValue<number>;
    bannerContent: ReactNode;
};

const Y_SPRING_CONFIG = {
    stiffness: 300,
    damping: 30,
    mass: 0.8,
};

const SCALE_SPRING_CONFIG = {
    stiffness: 200,
    damping: 25,
    mass: 1,
};

export function Slide({
    title,
    number,
    description,
    imageSrc,
    imageAlt,
    overlayImageUrl,
    overlayImageAlt,
    index,
    totalSlides,
    scrollYProgress,
    bannerContent,
}: SlideProps) {
    const { trackEvent } = useTrackEventAmplitude();

    const [hasReachedTop, setHasReachedTop] = useState(false);

    const slideRef = useRef<HTMLDivElement>(null);

    const isFirstSlide = index === 0;

    const slideStart = index / totalSlides;
    const slideAnimationDuration = 0.2;
    const slideEnd = slideStart + slideAnimationDuration;
    const nextSlideStart = Math.min((index + 1) / totalSlides, 1);
    const nextSlideHalf = nextSlideStart + (1 / totalSlides) * 0.9;

    useMotionValueEvent(scrollYProgress, 'change', (latest) => {
        if (!hasReachedTop && latest >= slideEnd) {
            setHasReachedTop(true);
            trackEvent((ampli, defaultProperties) => {
                ampli.blockViewed({
                    ...defaultProperties,
                    block: `parallax_step_${index + 1}` as
                        | 'parallax_step_1'
                        | 'parallax_step_2'
                        | 'parallax_step_3',
                });
            });
        }
    });

    const scaleTransform = useTransform(
        scrollYProgress,
        [slideStart, slideEnd, nextSlideStart, nextSlideHalf],
        [1, 1, 1, 0.7],
    );

    const yTransform = useTransform(
        scrollYProgress,
        [slideStart, slideEnd],
        isFirstSlide ? ['0vh', '0vh'] : ['100vh', '0vh'],
    );

    const scale = useSpring(scaleTransform, SCALE_SPRING_CONFIG);
    const y = useSpring(yTransform, Y_SPRING_CONFIG);

    return (
        <div ref={slideRef} className={style.slide}>
            <motion.div
                className={style.slideContent}
                style={{
                    scale,
                    y,
                    zIndex: index,
                }}
            >
                <div className={style.textContent}>
                    <div className={style.number}>{number}</div>
                    <div className={style.titleWrapper}>
                        <h3 className={style.title}>{title}</h3>
                        <div className={style.description}>{description}</div>
                    </div>
                </div>

                <div className={style.imageAndBanner}>
                    <div className={style.mediaContainer}>
                        {imageSrc ? (
                            <Image
                                className={style.image}
                                src={imageSrc}
                                alt={imageAlt || ''}
                                loader={imageLoaderForId({ aspectRatio: 16 / 9 })}
                                fill
                                sizes="(min-width: 1200px) 800px, (min-width: 992px) 700px, 500px"
                            />
                        ) : null}

                        {overlayImageUrl && (isFirstSlide || hasReachedTop) ? (
                            <Image
                                className={style.overlayImage}
                                src={pyriteImageStaticPath(overlayImageUrl)}
                                alt={overlayImageAlt || ''}
                                fill
                                unoptimized={true}
                            />
                        ) : null}
                    </div>
                    <div className={style.banner}>
                        <Typography scale="primary-xs-bold">{bannerContent}</Typography>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
